import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleHelp,
  Eye,
  Lightbulb,
  Sparkles
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { BirdPhoto } from "../components/BirdPhoto";
import { birdLabel, type Bird } from "../data/birds";
import type { Mastery } from "../hooks/useProgress";

type LearnViewProps = {
  cards: Bird[];
  mode: "daily" | "review";
  onBack: () => void;
  onRate: (id: string, mastery: Mastery) => void;
  onContinue: () => void;
};

export const LearnView = ({
  cards,
  mode,
  onBack,
  onRate,
  onContinue
}: LearnViewProps) => {
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [results, setResults] = useState<Mastery[]>([]);
  const bird = cards[index];
  const completed = index >= cards.length;

  const rate = (mastery: Mastery) => {
    if (!bird) return;
    onRate(bird.id, mastery);
    setResults((current) => [...current, mastery]);
    setRevealed(false);
    setIndex((current) => current + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (completed) return;
      if (!revealed && (event.key === " " || event.key === "Enter")) {
        event.preventDefault();
        setRevealed(true);
      }
      if (revealed && ["1", "2", "3"].includes(event.key)) {
        rate(
          event.key === "1" ? "new" : event.key === "2" ? "fuzzy" : "known"
        );
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  });

  const resultSummary = useMemo(
    () => ({
      known: results.filter((result) => result === "known").length,
      fuzzy: results.filter((result) => result === "fuzzy").length,
      learning: results.filter((result) => result === "new").length
    }),
    [results]
  );

  if (completed) {
    return (
      <main className="focus-page completion-page">
        <div className="completion-card">
          <span className="completion-card__burst" aria-hidden="true">
            <Sparkles size={34} />
          </span>
          <span className="eyebrow">本轮完成</span>
          <h1>{mode === "review" ? "记忆又扎实了一点" : "今天的鸟，已经见过一遍"}</h1>
          <p>
            认识不是一次答对，而是在下一次遇见时仍能抓住关键特征。
          </p>
          <div className="completion-stats">
            <div className="known">
              <Check size={19} />
              <strong>{resultSummary.known}</strong>
              <span>认识</span>
            </div>
            <div className="fuzzy">
              <AlertCircle size={19} />
              <strong>{resultSummary.fuzzy}</strong>
              <span>模糊</span>
            </div>
            <div className="new">
              <CircleHelp size={19} />
              <strong>{resultSummary.learning}</strong>
              <span>初见</span>
            </div>
          </div>
          <button className="button button--primary button--wide" onClick={onContinue}>
            回到学习首页
            <ChevronRight size={18} />
          </button>
        </div>
      </main>
    );
  }

  if (!bird) return null;

  const progress = Math.round(((index + (revealed ? 0.5 : 0)) / cards.length) * 100);

  return (
    <main className="focus-page learn-page">
      <header className="focus-header">
        <button className="icon-button" onClick={onBack} aria-label="退出学习">
          <ArrowLeft size={21} />
        </button>
        <div className="focus-progress">
          <span>
            {mode === "review" ? "巩固复习" : "今日学习"}
            <small>
              {index + 1} / {cards.length}
            </small>
          </span>
          <i>
            <b style={{ width: `${progress}%` }} />
          </i>
        </div>
        <span className="keyboard-hint">空格揭晓 · 1/2/3 评分</span>
      </header>

      <section className={`learn-card ${revealed ? "is-revealed" : ""}`}>
        <div className="learn-card__visual">
          <BirdPhoto
            images={bird.images}
            alt={revealed ? birdLabel(bird) : "待识别鸟类照片"}
            eager
          />
          <span className="photo-index">图卡 {bird.id}</span>
          {!revealed && (
            <div className="observation-prompt">
              <Eye size={19} />
              <span>
                <small>先别急着猜</small>
                看体形、嘴形，再找最醒目的色块
              </span>
            </div>
          )}
        </div>

        <div className="learn-card__content">
          {!revealed ? (
            <div className="guess-panel">
              <span className="section-kicker">{bird.group}</span>
              <h1>你认识这只鸟吗？</h1>
              <p>把第一判断留在心里，然后查看答案和辨识依据。</p>
              <button
                className="button button--primary button--wide reveal-button"
                onClick={() => setRevealed(true)}
              >
                <Eye size={19} />
                显示答案
              </button>
              <small className="guess-panel__tip">
                认不出来很正常——重点是记住揭晓后的“一眼特征”
              </small>
            </div>
          ) : (
            <div className="answer-panel">
              <div className="answer-panel__title">
                <span className="section-kicker">{bird.habitat}</span>
                <h1>{bird.name}</h1>
                {bird.variant && <strong>{bird.variant}</strong>}
              </div>
              <p className="bird-intro">{bird.intro}</p>
              <div className="memory-line">
                <Lightbulb size={21} />
                <span>
                  <small>记忆钩子</small>
                  {bird.memory}
                </span>
              </div>
              <div className="feature-block">
                <h2>一眼特征</h2>
                <div className="feature-chips">
                  {bird.keyMarks.map((mark, markIndex) => (
                    <span key={mark}>
                      <b>{markIndex + 1}</b>
                      {mark}
                    </span>
                  ))}
                </div>
              </div>
              <div className="compare-block">
                <span>VS</span>
                <div>
                  <small>最容易和谁混？</small>
                  <h2>{bird.similar.name}</h2>
                  <p>{bird.similar.difference}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {revealed && (
        <footer className="rating-bar">
          <span>现在，你对它的印象是？</span>
          <div>
            <button className="rating-button rating-button--new" onClick={() => rate("new")}>
              <CircleHelp size={19} />
              <span>
                不认识
                <small>按 1</small>
              </span>
            </button>
            <button
              className="rating-button rating-button--fuzzy"
              onClick={() => rate("fuzzy")}
            >
              <AlertCircle size={19} />
              <span>
                有点模糊
                <small>按 2</small>
              </span>
            </button>
            <button
              className="rating-button rating-button--known"
              onClick={() => rate("known")}
            >
              <CheckCircle2 size={19} />
              <span>
                认识
                <small>按 3</small>
              </span>
            </button>
          </div>
        </footer>
      )}
    </main>
  );
};
